// useLocation is safe for files not rendered in a partial on the overview.
// eslint-disable-next-line no-restricted-imports
import {useLocation} from '@github-ui/react-router'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {SearchIcon} from '@primer/octicons-react'
import {TextInput} from '@primer/react'
import {ScrollableRegion} from '@primer/react/experimental'
import {Box} from '@primer/styled-react'
import {useWindowVirtualizer} from '@tanstack/react-virtual'
import {clsx} from 'clsx'
import React, {useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react'

// eslint-disable-next-line no-restricted-imports
import {type BlobRange, parseFileAnchor} from '../../../../../github/blob-anchor'
import {useCurrentLineHeight} from '../../../../hooks/use-current-line-height'
import {isLineInViewport} from '../../../../utilities/lines'
import {
  hasHiddenUnicodeCharacters,
  HiddenUnicodeAlert,
  isHiddenUnicodeShown,
  showHiddenUnicodeCharacters,
} from '../../Banners/HiddenUnicode'
import type {CodeLinesHandle} from '../Code/code-lines-handle'
import {HighlightedLinesProvider, useHighlightedLinesInfo} from '../Code/HighlightedLineProvider'
import {LineNumber} from '../Code/LineNumber'
import {HighlightedLineMenuContainer} from '../HighlightedLineMenu'

interface CSVRowData {
  lineNumber: number
  cells: React.ReactNode[]
  highlighted: boolean
  hiddenUnicode: boolean
}

export default function CSVBlob({csv}: {csv: SafeHTMLString[][]}) {
  const tableHandle = useRef<CSVTableHandle>(null)
  const [highlightedLines, setHighlightedLines] = useHighlightedLines()
  const csvRows = toCSVData(csv, highlightedLines)
  const showHiddenUnicodeAlert = csvRows.some(row => row.hiddenUnicode)
  const {anchorPrefix} = parseFileAnchor(useLocation().hash)
  const onLineNumberClick = useCallback(() => {
    setHighlightedLines(parseFileAnchor(window.location.hash)?.blobRange)
  }, [setHighlightedLines])

  useEffect(() => {
    const anchorInfo = parseFileAnchor(window.location.hash)
    const table = tableHandle.current
    if (!highlightedLines || !table) return

    if (isLineInViewport(anchorInfo.blobRange.start.line)) return

    if (highlightedLines.start.line < 10) {
      // If the line is near the top, scroll to the top of the sticky thing
      tableHandle.current?.scrollToTop()
    } else {
      tableHandle.current?.scrollToLine(anchorInfo.blobRange.start.line)
    }
  }, [highlightedLines, anchorPrefix])

  return (
    <HighlightedLinesProvider highlightedLines={highlightedLines}>
      <CSVFilter onFilterChange={query => tableHandle.current?.setFilter(query)} />
      {showHiddenUnicodeAlert && <HiddenUnicodeAlert />}
      <HighlightedLineMenuContainer>
        <CSVTable ref={tableHandle} csvRows={csvRows} onLineNumberClick={onLineNumberClick} />
      </HighlightedLineMenuContainer>
    </HighlightedLinesProvider>
  )
}

function CSVFilter({onFilterChange}: {onFilterChange: (value: string) => void}) {
  return (
    <Box
      sx={{
        backgroundColor: 'var(--bgColor-muted, var(--color-canvas-subtle))',
        borderBottom: '1px solid var(--borderColor-default, var(--color-border-default))',
        position: 'relative',
        px: 3,
        py: 2,
      }}
    >
      <TextInput
        aria-label="Search this file"
        autoCapitalize="off"
        leadingVisual={SearchIcon}
        onChange={e => {
          const query = (e.target as HTMLInputElement).value.toLowerCase()
          onFilterChange(query)
        }}
        block
        name="filter"
        placeholder="Search this file"
        type="text"
      />
    </Box>
  )
}

interface CSVTableHandle extends CodeLinesHandle {
  setFilter: (query: string) => void
}

interface CSVTableProps {
  csvRows: readonly CSVRowData[]
  onLineNumberClick: React.MouseEventHandler<HTMLDivElement>
}

const CSVTable = React.forwardRef(CSVTableWithRef)

function CSVTableWithRef({csvRows, onLineNumberClick}: CSVTableProps, ref: React.ForwardedRef<CSVTableHandle>) {
  const [filter, setFilter] = useState<string | null>(null)
  const parentRef = useRef<HTMLTableElement>(null)
  const currentLineHeight = useCurrentLineHeight('react-csv-table-body', 35)

  const filteredCSVRows = filter
    ? csvRows.slice(1).filter(row => row.cells.join(' ').toLowerCase().includes(filter.toLowerCase()))
    : csvRows.slice(1)

  // Track parent offset for scrollMargin calculation
  const [parentOffset, setParentOffset] = useState(0)

  useLayoutEffect(() => {
    setParentOffset(parentRef.current?.offsetTop ?? 0)
  }, [])

  const virtualizer = useWindowVirtualizer({
    count: filteredCSVRows.length,
    overscan: 200,
    estimateSize: useCallback(() => currentLineHeight, [currentLineHeight]),
    scrollMargin: parentOffset,
  })

  useImperativeHandle(ref, () => ({
    scrollToTop: () => virtualizer.scrollToIndex(0, {align: 'start'}),
    scrollToLine: lineNumber => {
      const index = filteredCSVRows.findIndex(row => row.lineNumber === lineNumber)
      virtualizer.scrollToIndex(index, {align: 'start'})
    },
    setFilter,
  }))

  return (
    <ScrollableRegion aria-labelledby="LC1">
      <Box
        as="table"
        ref={parentRef}
        sx={{
          display: 'block',
          isolation: 'isolate',
        }}
        // Add the headers height to the total size
        style={{
          height: virtualizer.getTotalSize() + currentLineHeight,
          paddingTop: `${virtualizer.getVirtualItems()[0]?.start ?? 0}px`,
        }}
        data-hpc
        data-testid="csv-table"
      >
        {csvRows.length > 0 && (
          <>
            <CSVHeader
              hiddenUnicode={csvRows[0]!.hiddenUnicode}
              cells={csvRows[0]!.cells}
              onLineNumberClick={onLineNumberClick}
            />
            <tbody className="react-csv-table-body">
              {virtualizer.getVirtualItems().map(virtualItem => {
                const {cells, hiddenUnicode, lineNumber} = filteredCSVRows[virtualItem.index]!

                return (
                  <CSVRow
                    key={lineNumber}
                    lineNumber={lineNumber}
                    cells={cells}
                    hiddenUnicode={hiddenUnicode}
                    onLineNumberClick={onLineNumberClick}
                  />
                )
              })}
            </tbody>
          </>
        )}
      </Box>
    </ScrollableRegion>
  )
}

const CSVHeader = React.memo(CSVHeaderUnmemoized)

function CSVHeaderUnmemoized({
  cells,
  hiddenUnicode,
  onLineNumberClick,
}: {
  cells: React.ReactNode[]
  hiddenUnicode: boolean
  onLineNumberClick: React.MouseEventHandler<HTMLDivElement>
}) {
  return (
    <thead>
      <tr id={`LC1`} className="react-csv-row">
        <th className="react-csv-line-number">
          <LineNumber codeLineData={{lineNumber: 1, hiddenUnicode}} onClick={onLineNumberClick} />
        </th>
        {cells.map((cellContents, columnIndex) => {
          return (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <th className="react-csv-cell react-csv-cell--header" key={`0-${columnIndex}`}>
              {cellContents}
            </th>
          )
        })}
      </tr>
    </thead>
  )
}

const CSVRow = React.memo(CSVRowUnmemoized)

function CSVRowUnmemoized({
  lineNumber,
  cells,
  hiddenUnicode,
  onLineNumberClick,
}: {
  lineNumber: number
  cells: React.ReactNode[]
  hiddenUnicode: boolean
  onLineNumberClick: React.MouseEventHandler<HTMLDivElement>
}) {
  const highlightedLines = useHighlightedLinesInfo(lineNumber)
  const highlighted =
    !!highlightedLines && lineNumber >= highlightedLines.start.line && lineNumber <= highlightedLines.end.line
  const className = clsx('react-csv-row', highlighted && 'react-csv-row--highlighted')

  return (
    <tr id={`LC${lineNumber}`} className={className}>
      <td className="react-csv-line-number">
        <LineNumber codeLineData={{lineNumber, hiddenUnicode}} onClick={onLineNumberClick} />
      </td>
      {cells.map((cellContents, columnIndex) => {
        return (
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <td key={`${lineNumber}-${columnIndex}`} className="react-csv-cell">
            {cellContents}
          </td>
        )
      })}
    </tr>
  )
}

function toCSVData(csv: SafeHTMLString[][], highlightedLines: BlobRange | undefined): CSVRowData[] {
  return csv.map((row, rowIndex) => {
    const hiddenUnicodeShown = isHiddenUnicodeShown()
    const hiddenUnicode = hasHiddenUnicodeCharacters(row.join(''))
    const cells =
      hiddenUnicodeShown && hiddenUnicode
        ? row.map(cell => (cell ? showHiddenUnicodeCharacters(cell) ?? cell : cell))
        : row

    const lineNumber = rowIndex + 1
    const highlighted =
      !!highlightedLines && lineNumber >= highlightedLines.start.line && lineNumber <= highlightedLines.end.line

    return {lineNumber, cells, hiddenUnicode, highlighted}
  })
}

function useHighlightedLines(): [BlobRange | undefined, React.Dispatch<React.SetStateAction<BlobRange | undefined>>] {
  const {hash} = useLocation()
  const initialHighlightedLines = parseFileAnchor(hash).blobRange
  const highlightedLinesRef = useRef<BlobRange | undefined>(initialHighlightedLines)
  const [highlightedLines, setHighlightedLines] = useState<BlobRange | undefined>(initialHighlightedLines)

  useEffect(() => {
    const newBlobRange = parseFileAnchor(window.location.hash).blobRange

    if (
      newBlobRange?.start.line !== highlightedLinesRef.current?.start.line ||
      newBlobRange?.end.line !== highlightedLinesRef.current?.end.line
    ) {
      highlightedLinesRef.current = newBlobRange
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHighlightedLines(newBlobRange)
    }
  }, [hash])

  return [highlightedLines, setHighlightedLines]
}

try{ CSVBlob.displayName ||= 'CSVBlob' } catch {}
try{ CSVFilter.displayName ||= 'CSVFilter' } catch {}
try{ CSVTable.displayName ||= 'CSVTable' } catch {}
try{ CSVTableWithRef.displayName ||= 'CSVTableWithRef' } catch {}
try{ CSVHeader.displayName ||= 'CSVHeader' } catch {}
try{ CSVHeaderUnmemoized.displayName ||= 'CSVHeaderUnmemoized' } catch {}
try{ CSVRow.displayName ||= 'CSVRow' } catch {}
try{ CSVRowUnmemoized.displayName ||= 'CSVRowUnmemoized' } catch {}